/**
 * Blended ARR pacing math (pure — unit-tested).
 *
 * The locked model (2026-06-10): Scope app subscriptions with ≥10% incremental
 * Accelerator upsell after 1–3 months plus seat expansion. The goal row's
 * jsonb pacing_model carries the founder-editable assumptions; this module
 * turns them into the numbers the Brief renders. No invented figures: every
 * output is arithmetic over the stored assumptions and live DB rollups.
 */

export interface PacingModel {
  scope_arpu_usd: number;
  accelerator_arpu_usd: number;
  accel_upsell_rate: number;
  accel_lag_months_min: number;
  accel_lag_months_max: number;
}

export const DEFAULT_PACING_MODEL: PacingModel = {
  scope_arpu_usd: 115,
  accelerator_arpu_usd: 1300,
  accel_upsell_rate: 0.1,
  accel_lag_months_min: 1,
  accel_lag_months_max: 3,
};

/** Linear pace from 0 at `start` to `target` at `end`, clamped to [0, target]. */
export function expectedOnPace(
  targetValue: number,
  startIso: string,
  endIso: string,
  nowIso: string,
): number {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  const frac = (now - start) / (end - start);
  return Math.min(Math.max(frac, 0), 1) * targetValue;
}

/**
 * Steady-state account math: each paying Scope account contributes its ARPU
 * plus (upsell_rate × Accelerator ARPU) once the 1–3-month lag has played out.
 */
export function steadyState(model: PacingModel, targetArrUsd: number): {
  blendedMrrPerAccount: number;
  accountsNeeded: number;
  appMrrShare: number;
  accelMrrShare: number;
} {
  const blended = model.scope_arpu_usd + model.accel_upsell_rate * model.accelerator_arpu_usd;
  const targetMrr = targetArrUsd / 12;
  const accounts = blended > 0 ? Math.ceil(targetMrr / blended) : 0;
  const appShare = blended > 0 ? model.scope_arpu_usd / blended : 0;
  return {
    blendedMrrPerAccount: blended,
    accountsNeeded: accounts,
    appMrrShare: appShare,
    accelMrrShare: 1 - appShare,
  };
}

/** Gap between actual and on-pace expected (negative = behind). */
export function paceGap(actualArr: number, expectedArr: number): {
  gapUsd: number;
  ratio: number; // 1.0 = exactly on pace; 0 when expected is 0 and actual is 0
} {
  const gap = actualArr - expectedArr;
  const ratio = expectedArr > 0 ? actualArr / expectedArr : actualArr > 0 ? Infinity : 1;
  return { gapUsd: gap, ratio };
}
